import * as THREE from 'three';
import { IModelGroupData } from '../data/modelGroup/modelGroup';
import { globalStores } from '../stores';
import { EEntityType, EOperateMode, ESceneMode, EToolType, ISelected } from '../types';
import editorEvent, { EditorEventName } from '../event/editorEvent';
import { cloneDeep } from 'lodash-es';
import { Point2dUtils } from '../math/Point2dUtils';
import { IPoint2d } from '../math/IPoint2d';
import { IAssemblyData } from '../data/assembly/assembly';
import { FLAGS } from '@/flags';

function checkMoveByKeyboardAllowed(keys: string[]): boolean {
  if (!globalStores.keyboardStore.checkKeysPressed(keys)) {
    return false;
  }

  if (!globalStores.keyboardStore.isScopeAllowMoveByKeyboard()) {
    return false;
  }

  // 检查是否在输入状态（输入框获得焦点时不移动）
  const activeEl = document.activeElement;
  if (
    activeEl &&
    (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || (activeEl as HTMLElement).isContentEditable)
  ) {
    return false;
  }

  return true;
}

export function createKeyboardMoveUpdate(sceneMode: ESceneMode, target: THREE.Vector3): (() => void) | null {
  if (!FLAGS.FEATURES.ENABLE_SHORTCUT_V1) {
    return null;
  }

  const clock = new THREE.Clock();
  const SPEED = 4; // 移动速度，可以根据需要调整
  const keyboardStore = globalStores.keyboardStore;

  switch (sceneMode) {
    case ESceneMode.scene3d: {
      return () => {
        if (!checkMoveByKeyboardAllowed(['w', 'a', 's', 'd', 'q', 'e'])) {
          return;
        }
        const camera = globalStores.editorStateStore.sceneInfo.scene3d.camera;

        const moveSpeed = SPEED * clock.getDelta();

        // 计算相机方向向量（水平面投影）
        const forward = new THREE.Vector3();
        camera.getWorldDirection(forward);
        forward.z = 0; // 投影到水平面
        forward.normalize();

        // 计算右方向向量（水平面）
        const right = new THREE.Vector3();
        right.crossVectors(forward, new THREE.Vector3(0, 0, 1)).normalize();

        // 累积移动向量
        const moveVector = new THREE.Vector3();

        // W/S: 前后移动（沿相机朝向的水平投影）
        if (keyboardStore.isKeyPressed('w', false)) {
          moveVector.add(forward.clone().multiplyScalar(moveSpeed));
        }
        if (keyboardStore.isKeyPressed('s', false)) {
          moveVector.add(forward.clone().multiplyScalar(-moveSpeed));
        }

        // A/D: 左右移动
        if (keyboardStore.isKeyPressed('a', false)) {
          moveVector.add(right.clone().multiplyScalar(-moveSpeed));
        }
        if (keyboardStore.isKeyPressed('d', false)) {
          moveVector.add(right.clone().multiplyScalar(moveSpeed));
        }

        // Q/E: 上下移动（沿世界 Z 轴）
        if (keyboardStore.isKeyPressed('q', false)) {
          moveVector.z -= moveSpeed;
        }
        if (keyboardStore.isKeyPressed('e', false)) {
          moveVector.z += moveSpeed;
        }

        // 应用移动
        if (moveVector.lengthSq() > 0) {
          camera.position.add(moveVector);
          target.add(moveVector);
          editorEvent.emit(EditorEventName.VIEW3D_CHANGE);
        }
      };
    }
    case ESceneMode.scene2d: {
      return () => {
        if (!checkMoveByKeyboardAllowed(['w', 'a', 's', 'd'])) {
          return;
        }
        const camera = globalStores.editorStateStore.sceneInfo.scene2d.camera;

        const moveSpeed = SPEED * clock.getDelta();

        let dx = 0;
        let dy = 0;

        // W/S: 上下移动（Y 轴）
        if (keyboardStore.isKeyPressed('w', false)) {
          dy += moveSpeed;
        }
        if (keyboardStore.isKeyPressed('s', false)) {
          dy -= moveSpeed;
        }

        // A/D: 左右移动（X 轴）
        if (keyboardStore.isKeyPressed('a', false)) {
          dx -= moveSpeed;
        }
        if (keyboardStore.isKeyPressed('d', false)) {
          dx += moveSpeed;
        }

        // 应用移动
        if (dx !== 0 || dy !== 0) {
          camera.position.x += dx;
          camera.position.y += dy;
          target.x += dx;
          target.y += dy;
          editorEvent.emit(EditorEventName.VIEW2D_CHANGE);
        }
      };
    }
    default:
      return null;
  }
}

export function moveModelGroupInfo(point: IPoint2d, modelGroupData: IModelGroupData) {
  const offset = Point2dUtils.subtracted(point, modelGroupData.center);
  for (const accessorieId of modelGroupData.accessorieIds) {
    const furnitureData = globalStores.modelStore.getFurnitureData(accessorieId);
    if (furnitureData) {
      const newFurnitureData = cloneDeep(furnitureData);
      newFurnitureData.center = Point2dUtils.added(furnitureData.center, offset);
      globalStores.furnitureOperation.modifyFurniture(accessorieId, newFurnitureData);
    }
  }

  const newModelGroupData = cloneDeep(modelGroupData);
  newModelGroupData.center = point;

  return { newModelGroupData };
}

export function moveAssembly(point: IPoint2d, assemblyData: IAssemblyData) {
  const moveAssemblyData = cloneDeep(assemblyData);
  moveAssemblyData.center = point;

  return moveAssemblyData;
}

/**
 * 把这一次左键按下交接给多选控制器
 *
 * 切模式是同步执行的，调用方在这里就被 dispose 了，必须立刻 return。
 * 新控制器按 DOM 规范收不到正在派发的这一个鼠标事件，所以用事件把它补发过去。
 */
export function handOverMouseDownToMultiSelect(event: MouseEvent) {
  globalStores.operateInfo = { mode: EOperateMode.MultiSelect, toolType: EToolType.empty };
  editorEvent.emit(EditorEventName.MULTI_SELECT_TAKEOVER_MOUSE_DOWN, event);
}

/** 判断当前 entity 是否存在多选数据中 */
export function isInMultiModel(data: Pick<ISelected, 'entityId' | 'type'>) {
  const { entityId, type } = data;
  if (type === EEntityType.MultiModel) return true;

  const multiModelData = globalStores.getDataByIdType('', EEntityType.MultiModel);
  if (!multiModelData) return false;

  const modelInfos = multiModelData.modelInfos;
  for (const modelInfo of modelInfos) {
    if (modelInfo.id === entityId) return true;
  }
  return false;
}
